import { getTodoListDoneSectionName, TODO_LIST_GROUP_ICONS } from '../types';

import type { TodoItemForSidebar, TodoListForSidebar } from '@erikdakoda/todo/types';
import type { LabelSidebarItem, LabelSidebarSection } from '@erikdakoda/label-ui/types';

/** Marks the synthetic Done section id so it can be filtered out of a list reorder. */
export const TODO_DONE_SECTION_SUFFIX = '-done';

/** One todo list of a group with its already name-filtered rows. */
export type TodoGroupListEntry = {
  list: TodoListForSidebar;
  items: readonly TodoItemForSidebar[];
  /** Completed rows for `group === 'user'` lists; rendered as a sibling Done section. */
  doneItems?: readonly TodoItemForSidebar[];
};

export type BuildTodoSectionsOptions = {
  lists: readonly TodoGroupListEntry[];
  /** Owner of the rows; carried on every item for the shared sidebar's item shape. */
  ownerId: string;
  spaceId: string | null;
};

/**
 * Id of the synthetic Done section that sits under a user todo list.
 * @param todoListId The list whose completed rows the section holds.
 * @returns The list id with the synthetic-section suffix appended.
 */
export function todoDoneSectionId(todoListId: string): string {
  return `${todoListId}${TODO_DONE_SECTION_SUFFIX}`;
}

function toSidebarItems(
  items: readonly TodoItemForSidebar[],
  ownerId: string,
  spaceId: string | null,
): LabelSidebarItem[] {
  return items.map((item, index) => ({
    id: item.id,
    name: item.name,
    ord: index,
    ownerId,
    spaceId,
  }));
}

/**
 * Builds the shared-sidebar sections for one todo group: a section per `TodoList`, plus the
 * synthetic, non-droppable Done section under each user list.
 * @param options Group lists with their filtered rows, plus the owner of those rows.
 * @returns Sections in list order, ready for `LabelSidebar`.
 */
export function buildTodoSections(options: BuildTodoSectionsOptions): LabelSidebarSection[] {
  const { lists, ownerId, spaceId } = options;
  const sections: LabelSidebarSection[] = [];

  for (const { list, items, doneItems } of lists) {
    sections.push({
      id: list.id,
      kind: 'main',
      name: list.name,
      icon: list.icon ?? TODO_LIST_GROUP_ICONS[list.group] ?? 'clipboard-list',
      iconPrefix: list.iconPrefix ?? undefined,
      items: toSidebarItems(items, ownerId, spaceId),
      droppable: true,
      locked: false,
    });

    if (doneItems === undefined) {
      continue;
    }

    sections.push({
      id: todoDoneSectionId(list.id),
      kind: 'main',
      name: getTodoListDoneSectionName(list.name),
      icon: 'square-check',
      iconPrefix: 'fal',
      items: toSidebarItems(doneItems, ownerId, spaceId),
      droppable: false,
      locked: true,
      itemsDraggable: false,
      sectionDraggable: false,
    });
  }

  return sections;
}

/**
 * Todo list ids in the order a section reorder produced, dropping the synthetic Done sections.
 * @param sectionIds Section ids in their new order.
 * @param todoListIds Ids of the real todo lists in the group.
 * @returns The reordered todo list ids.
 */
export function toTodoListOrder(
  sectionIds: readonly string[],
  todoListIds: readonly string[],
): string[] {
  const known = new Set(todoListIds);
  return sectionIds.filter((id) => known.has(id));
}
